import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tabs,
  TabList,
  Tab,
  Input,
  Flex,
  HStack,
  Button,
  useBreakpointValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import DatePicker from 'react-datepicker'; // Make sure to import DatePicker
import 'react-datepicker/dist/react-datepicker.css'; // Import the CSS for DatePicker
import { useDispatch, useSelector } from 'react-redux';
import viewJob from './viewJob';
import { fetchJobHistory, deleteJobFromHistory } from '../../store/slices/historySlice'; // Import the thunk

const HistoryTable = ({ onViewJob }) => {
  const dispatch = useDispatch();
  const { historyData, loading, error } = useSelector((state) => state.history);
  const [filter, setFilter] = useState('All Posts');
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const cancelRef = useRef(); // Ref to handle focus on cancel button
  const itemsPerPage = 5;
  const paginationButtonSize = useBreakpointValue({ base: "xs", md: "sm" }); // Define responsive button size
  const paginationSpacing = useBreakpointValue({ base: 2, md: 4 }); // Define responsive spacing
  const inputSize = useBreakpointValue({ base: "sm", md: "md" }); // Define responsive input size
  const [activeTab, setActiveTab] = useState('All Posts');
  const [isNothingToClearDialogOpen, setIsNothingToClearDialogOpen] = useState(false);

  // Helper functions need to be defined before they're used
  const startOfDay = (date) => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };

  const endOfDay = (date) => {
    const newDate = new Date(date);
    newDate.setHours(23, 59, 59, 999);
    return newDate;
  };

  useEffect(() => {
    dispatch(fetchJobHistory());
  }, [dispatch]);

  const filteredData = Array.isArray(historyData) ? historyData
    .filter((item) => {
      // First filter by status
      const statusMatch = 
        filter === 'All Posts' ? true :
        filter === 'Completed' ? item.status.toLowerCase() === 'completed' :
        filter === 'Cancelled' ? item.status.toLowerCase() === 'cancelled' :
        filter === 'Expired' ? item.status.toLowerCase() === 'expired' : true;

      // Then filter by date range
      const itemDate = new Date(item.jobStartDate);
      const fromDateMatch = fromDate ? itemDate >= startOfDay(fromDate) : true;
      const toDateMatch = toDate ? itemDate <= endOfDay(toDate) : true;

      return statusMatch && fromDateMatch && toDateMatch;
    }) : [];

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true); // Open the delete confirmation dialog
  };

  const handleCloseDialog = () => {
    setIsDeleteDialogOpen(false);
    setItemToDelete(null); // Reset item to delete
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      dispatch(deleteJobFromHistory(itemToDelete._id)); // Dispatch the delete action
    }
    setIsDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleViewJob = (jobId) => {
    onViewJob(jobId);
  };

  const handleClearAll = () => {
    if (historyData.length === 0) {
      // Show an alert dialog indicating there is nothing to clear
      setIsNothingToClearDialogOpen(true);
    } else {
      // Open the confirmation dialog to clear all items
      setIsDialogOpen(true);
    }
  };

  const handleClearAllJobs = () => {
    const jobIds = historyData.map(job => job._id); // Extract all job IDs
    dispatch(deleteJobFromHistory(jobIds)) // Dispatch the delete action for all jobs
      .then(() => {
        // Clear the local state to immediately reflect the changes in the UI
        dispatch({ type: 'history/clearHistory' }); // Assuming you have an action to clear history in the Redux store
      });
    setIsDialogOpen(false); // Close the dialog after clearing
  };

  const handleFromDateChange = (date) => {
    setFromDate(date);
    setCurrentPage(1);
  };

  const handleToDateChange = (date) => {
    setToDate(date);
    setCurrentPage(1);
  };

  return (
    <Box w="100%" p={1} display="flex" flexDirection="column" minH="90vh" justifyContent="space-between">
      {loading && <p>Loading...</p>}
      {error && <p>Error: {error}</p>}

      {/* Nothing to clear alert dialog */}
      <AlertDialog
        isOpen={isNothingToClearDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsNothingToClearDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              No Items to Clear
            </AlertDialogHeader>

            <AlertDialogBody>
              There are no job entries to clear.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsNothingToClearDialogOpen(false)}>
                OK
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Delete confirmation dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleCloseDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Confirmation
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this item? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={isDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Clear All
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete all job entries? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleClearAllJobs} ml={3}>
                Clear All
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Date range filter */}
      <Flex justifyContent="space-between" mb={4} alignItems="center">
        <Button
          onClick={handleClearAll} // Use the updated handleClearAll function
          size={inputSize}
          variant="ghost"
          color="blue.500"
        >
          Clear All
        </Button>
        <HStack spacing={4}>
          <DatePicker
            selected={fromDate}
            onChange={handleFromDateChange}
            customInput={<Input placeholder="dd/mm/yyyy" size={inputSize} width={inputSize === "sm" ? "110px" : "140px"} />}
            dateFormat="dd/MM/yyyy"
            placeholderText="From"
            isClearable
          />
          <DatePicker
            selected={toDate}
            onChange={handleToDateChange}
            customInput={<Input placeholder="dd/mm/yyyy" size={inputSize} width={inputSize === "sm" ? "110px" : "140px"} />}
            dateFormat="dd/MM/yyyy"
            placeholderText="To"
            isClearable
            popperPlacement="bottom-end"
          />
        </HStack>
      </Flex>

      {/* Title and tabs */}
      <Box mb={4}>
        <Tabs
          variant="soft-rounded"
          colorScheme="red"
          mb={4}
          onChange={(index) =>
            setFilter(index === 0 ? 'All Posts' : index === 1 ? 'Completed' : index === 2 ? 'Cancelled' : 'Expired')
          }
        >
          <TabList gap={4} justifyContent="left">
            <Tab
              borderRadius="md"
              position="relative"
              _hover={{
                backgroundColor: 'transparent',
                textDecoration: 'none',
              }}
              _selected={{
                backgroundColor: 'transparent',
                textDecoration: 'none',
                fontWeight: 'bold',
                color: 'black',
              }}
              color="black"
              px={6}
              onClick={() => setActiveTab('All Posts')}
            >
              All Posts
              {activeTab === 'All Posts' && (
                <Box
                  position="absolute"
                  bottom="-2px"
                  left="0"
                  right="0"
                  height="2px" // Adjust height as needed
                  bg="blue.500"
                  borderRadius="2px 2px 0 0" // Add border radius to top corners
                  clipPath="polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)" // Adjust this line
                />
              )}
            </Tab>
            <Tab
              borderRadius="md"
              position="relative"
              _hover={{
                backgroundColor: 'transparent',
                textDecoration: 'none',
              }}
              _selected={{
                backgroundColor: 'transparent',
                textDecoration: 'none',
                fontWeight: 'bold',
                color: 'black',
              }}
              color="black"
              px={6}
              onClick={() => setActiveTab('Completed')}
            >
              Completed
              {activeTab === 'Completed' && (
                <Box
                  position="absolute"
                  bottom="-2px"
                  left="0"
                  right="0"
                  height="2px"
                  bg="blue.500"
                  borderRadius="3px 3px 0 0" // Add border radius to top corners
                  clipPath="polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)" // Adjust this line
                />
              )}
            </Tab>
            <Tab
              borderRadius="md"
              position="relative"
              _hover={{
                backgroundColor: 'transparent',
                textDecoration: 'none',
              }}
              _selected={{
                backgroundColor: 'transparent',
                textDecoration: 'none',
                fontWeight: 'bold',
                color: 'black',
              }}
              color="black"
              px={6}
              onClick={() => setActiveTab('Cancelled')}
            >
              Cancelled
              {activeTab === 'Cancelled' && (
                <Box
                  position="absolute"
                  bottom="-2px"
                  left="0"
                  right="0"
                  height="2px"
                  bg="blue.500"
                  borderRadius="3px 3px 0 0" // Add border radius to top corners
                  clipPath="polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)" // Adjust this line
                />
              )}
            </Tab>
            <Tab
              borderRadius="md"
              position="relative"
              _hover={{
                backgroundColor: 'transparent',
                textDecoration: 'none',
              }}
              _selected={{
                backgroundColor: 'transparent',
                textDecoration: 'none',
                fontWeight: 'bold',
                color: 'black',
              }}
              color="black"
              px={6}
              onClick={() => setActiveTab('Expired')}
            >
              Expired
              {activeTab === 'Expired' && (
                <Box
                  position="absolute"
                  bottom="-2px"
                  left="0"
                  right="0"
                  height="2px"
                  bg="blue.500"
                  borderRadius="3px 3px 0 0"
                  clipPath="polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                />
              )}
            </Tab>
          </TabList>
        </Tabs>
      </Box>

      {/* Table with horizontal scroll */}
      <Box flexGrow={1} overflowY="auto">
        <Table variant="simple" width="100%" minWidth="800px">
          <Thead bgColor="gray.100">
            <Tr>
              <Th p={4}>Job Title</Th>
              <Th p={4}>Start Date</Th>
              <Th p={4}>End Date</Th>
              <Th p={4}>Status</Th>
              <Th p={4}>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {currentData.length > 0 ? (
              currentData.map((item, index) => (
                <Tr
                  key={index}
                  onClick={() => handleViewJob(item._id)}
                  _hover={{
                    transform: 'translateY(-5px)', // Moves the row up by 5 pixels
                    boxShadow: 'lg', // Adds a large shadow to the row
                    transition: 'transform 0.2s ease-in-out', // Smooth transition effect
                    bg: '#F5F6F8' // Changes the background color
                  }}
                >
                  <Td p={4}>{item.jobTitle}</Td>
                  <Td p={4}>{new Date(item.jobStartDate).toLocaleDateString()}</Td>
                  <Td p={4}>{new Date(item.jobEndDate).toLocaleDateString()}</Td>
                  <Td p={4} color={item.status.toLowerCase() === 'completed' ? 'green.500' : 'red.500'}>
                    {item.status}
                  </Td>
                  <Td p={4}>
                    <IconButton
                      icon={<DeleteIcon />}
                      aria-label="Delete job"
                      variant="outline"
                      onClick={(event) => {
                        event.stopPropagation(); // Prevents the row click event
                        handleDeleteClick(item);
                      }}
                      color="black"
                      bg="transparent"
                    />
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={5} textAlign="center" p={4}>
                  No entries found.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination Component */}
      <HStack spacing={paginationSpacing} mt={6} mb={4} justifyContent="center" flexWrap="wrap">
        <Button variant="ghost" isDisabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} color="gray.700" size={paginationButtonSize}>
          Previous
        </Button>
        {Array.from({ length: totalPages }, (_, i) => (
          <Button
            key={i + 1}
            colorScheme={currentPage === i + 1 ? 'gray' : 'gray'}
            bg={currentPage === i + 1 ? 'gray.500' : 'gray.200'}
            color={currentPage === i + 1 ? 'white' : 'black'}
            onClick={() => handlePageChange(i + 1)}
            borderRadius="md"
            width={paginationButtonSize === 'xs' ? '30px' : '40px'}
            size={paginationButtonSize}
          >
            {i + 1}
          </Button>
        ))}
        <Button variant="ghost" isDisabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} color="gray.700" size={paginationButtonSize}>
          Next
        </Button>
      </HStack>
    </Box>
  );
};

export default HistoryTable;